import { useEffect, useMemo, useState } from "react";
import COUNTRY_CODES, { parsePhone, findByIso, detectBrowserCountryIso } from "@/lib/countryCodes";
import { useLocale } from "@/i18n/useLocale";

/**
 * Native <select> + <input type="tel"> phone picker.
 *
 * - Composes `+{dial}{number}` into a single string and forwards it to
 *   `onChange(composed)`. The parent stores the composed value in the same
 *   field it always did (e.g. `contact.phone`) — no schema change.
 * - On mount, if `value` already contains a country prefix, the picker
 *   parses it and preselects that country. Otherwise it falls back to
 *   `defaultCountryIso` (e.g. from `identity.country`).
 * - No external dependencies. Emoji flags render via native OS fonts.
 */
export default function PhoneInput({
  value = "",
  onChange,
  defaultCountryIso = "",
  placeholder = "",
  testId = "phone-input",
}) {
  const { t } = useLocale();

  const initial = useMemo(() => {
    const parsed = parsePhone(value);
    if (parsed.country) return parsed;
    // Fallback chain: explicit prop → navigator.language → timezone → US.
    // We log the trace once at mount so devs can inspect resolution in the
    // browser console if a user reports the wrong default.
    const detected = detectBrowserCountryIso(defaultCountryIso);
    if (typeof window !== "undefined" && window.console) {
      console.log("[PhoneInput] iso resolution:", detected.trace, "source=", detected.source);
    }
    const fallback = findByIso(detected.iso) || COUNTRY_CODES[0];
    return { country: fallback, national: value?.replace(/^\+/, "") || "" };
  }, []);

  const [country, setCountry] = useState(initial.country);
  const [national, setNational] = useState(initial.national);

  // If the parent replaces `value` (e.g. reset after save), re-sync.
  useEffect(() => {
    const parsed = parsePhone(value);
    if (parsed.country) {
      setCountry(parsed.country);
      setNational(parsed.national);
    } else if (!value) {
      setNational("");
    }
  }, [value]);

  const emit = (nextCountry, nextNational) => {
    const digits = (nextNational || "").replace(/[^0-9]/g, "");
    const composed = digits ? `+${nextCountry.dial}${digits}` : "";
    onChange?.(composed);
  };

  const onCountry = (iso) => {
    const next = findByIso(iso) || country;
    setCountry(next);
    emit(next, national);
  };

  const onNumber = (raw) => {
    // Allow spaces + dashes visually but strip on emit.
    setNational(raw);
    emit(country, raw);
  };

  return (
    <div className="flex w-full items-stretch overflow-hidden rounded-md border border-white/15 bg-transparent">
      <select
        value={country.iso}
        onChange={(e) => onCountry(e.target.value)}
        aria-label={t("createCard.phoneCountry") || "Country"}
        data-testid={`${testId}-country`}
        className="max-w-[9rem] shrink-0 border-r border-white/10 bg-[#0A0B0D] px-2 py-2 text-sm text-white outline-none focus:bg-[#111214]"
        style={{ fontFamily: "inherit" }}
      >
        {COUNTRY_CODES.map((c) => (
          <option key={c.iso} value={c.iso}>
            {c.flag} +{c.dial} {c.name}
          </option>
        ))}
      </select>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={national}
        onChange={(e) => onNumber(e.target.value)}
        placeholder={placeholder || "5551234567"}
        data-testid={`${testId}-number`}
        className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-white outline-none placeholder:text-white/30"
      />
    </div>
  );
}
